// API Client for TradeOS Dashboard
//
// This module provides a typed API client for communicating with the TradeOS backend.
// It handles:
// - Authentication via Bearer tokens
// - Request timeout handling
// - Error response transformation
// - Response data transformation (snake_case → camelCase)
//
// GAP-KILLSWITCH-001: Path mismatch - Backend has /api/kill-switch/* aliases
// GAP-KILLSWITCH-002: Response format - Backend returns {active: boolean}
// GAP-PROPOSALS-001: Naming mismatch - Backend has /api/proposals/* aliases
// GAP-TYPES-001: Snake_case vs camelCase - Handled via transforms.ts
// GAP-TYPES-002: Field naming inconsistencies - Handled via transforms.ts
//
// @see doc/code-review/gap-analysis/ui-backend-integration-gaps.md

import type {
  ApiResponse,
  Portfolio,
  Position,
  Order,
  Trade,
  Signal,
  Ticker,
  Candle,
  OrderBook,
  RiskMetrics,
  RiskLimit,
  SystemStatus,
  Alert,
  ChartData,
  UserSettings,
} from '../types/api';

import {
  transformPosition,
  transformPositions,
  transformOrder,
  transformOrders,
  transformTrade,
  transformTrades,
  transformSignal,
  transformSignals,
  transformRiskMetrics,
  transformSystemStatus,
  transformKillSwitchStatus,
  transformKillSwitchAction,
  transformProposal,
  transformProposals,
  transformError,
  getUserFriendlyErrorMessage,
  type TransformedPosition,
  type TransformedOrder,
  type TransformedTrade,
  type TransformedSignal,
  type TransformedRiskMetrics,
  type TransformedSystemStatus,
  type TransformedKillSwitchStatus,
  type TransformedProposal,
  type TransformedApiError,
} from './transforms';

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_TIMEOUT = 30000;

// Custom error class
class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Request helper
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  const token = localStorage.getItem('auth_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || `HTTP error! status: ${response.status}`,
        errorData
      );
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof ApiError) {
      throw error;
    }
    
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Request timeout');
    }
    
    throw new ApiError(0, error instanceof Error ? error.message : 'Unknown error');
  }
}

// Portfolio API
export const portfolioApi = {
  get: () => request<ApiResponse<Portfolio>>('/api/portfolio'),
  
  getPositions: () => request<ApiResponse<Position[]>>('/api/portfolio/positions'),
  
  getPosition: (symbol: string) =>
    request<ApiResponse<Position>>(`/api/portfolio/positions/${symbol}`),
  
  getHistory: (range: string = '30d') =>
    request<ApiResponse<{ date: string; value: number }[]>>(
      `/api/portfolio/history?range=${range}`
    ),
};

// Orders API
export const ordersApi = {
  getAll: (params?: { status?: string; symbol?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return request<ApiResponse<Order[]>>(
      `/api/orders${query ? `?${query}` : ''}`
    );
  },
  
  get: (id: string) => request<ApiResponse<Order>>(`/api/orders/${id}`),
  
  create: (order: Partial<Order>) =>
    request<ApiResponse<Order>>('/api/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    }),
  
  cancel: (id: string) =>
    request<ApiResponse<void>>(`/api/orders/${id}/cancel`, {
      method: 'POST',
    }),
  
  modify: (id: string, modifications: Partial<Order>) =>
    request<ApiResponse<Order>>(`/api/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(modifications),
    }),
};

// Trades API
export const tradesApi = {
  getAll: (params?: { symbol?: string; limit?: number }) => {
    const query = new URLSearchParams(
      Object.entries(params || {}).reduce((acc, [k, v]) => {
        if (v !== undefined) acc[k] = String(v);
        return acc;
      }, {} as Record<string, string>)
    ).toString();
    return request<ApiResponse<Trade[]>>(
      `/api/trades${query ? `?${query}` : ''}`
    );
  },
  
  get: (id: string) => request<ApiResponse<Trade>>(`/api/trades/${id}`),
};

// Signals API
export const signalsApi = {
  getActive: () => request<ApiResponse<Signal[]>>('/api/signals/active'),
  
  getHistory: (params?: { symbol?: string; limit?: number }) => {
    const query = new URLSearchParams(
      Object.entries(params || {}).reduce((acc, [k, v]) => {
        if (v !== undefined) acc[k] = String(v);
        return acc;
      }, {} as Record<string, string>)
    ).toString();
    return request<ApiResponse<Signal[]>>(
      `/api/signals/history${query ? `?${query}` : ''}`
    );
  },

  /**
   * Get signals filtered by view layer
   * GET /api/signals?layer={layer}
   * @param layer - View layer for progressive disclosure (simple, detailed, audit)
   * @param params - Optional filter parameters
   */
  getByLayer: (layer: 'simple' | 'detailed' | 'audit', params?: { limit?: number }) => {
    const queryParams = new URLSearchParams({ layer });
    if (params?.limit) {
      queryParams.set('limit', String(params.limit));
    }
    return request<SignalsLayerResponse>(`/api/signals?${queryParams.toString()}`);
  },
};

/**
 * Response type for signals with layer support
 */
export interface SignalsLayerResponse {
  items: SignalLayerItem[];
  total: number;
  layer: string;
  timestamp: string;
}

/**
 * Signal item with layer-aware fields from backend
 */
export interface SignalLayerItem {
  id: string;
  symbol: string;
  direction: 'long' | 'short' | 'buy' | 'sell';
  signal_type: string;
  strategy_id: string;
  confidence: number;
  issued_at: string;
  expires_at: string;
  ttl_seconds: number;
  verdict: 'APPROVED' | 'REJECTED' | 'SIZED_DOWN';
  base_multiplier: number;
  modifier_multiplier: number;
  recommended_size_usd: number;
  sizing_adjustment: number;
  tags: string[];
  reasons: string[];
  metadata?: Record<string, unknown>;
}

// Market Data API
export const marketApi = {
  getTicker: (symbol: string) =>
    request<ApiResponse<Ticker>>(`/api/market/ticker/${symbol}`),
  
  getTickers: (symbols: string[]) =>
    request<ApiResponse<Ticker[]>>(
      `/api/market/tickers?symbols=${symbols.join(',')}`
    ),
  
  getCandles: (
    symbol: string,
    interval: string = '1h',
    limit: number = 100
  ) =>
    request<ApiResponse<Candle[]>>(
      `/api/market/candles/${encodeURIComponent(symbol)}?interval=${interval}&limit=${limit}`
    ),
  
  getOrderBook: (symbol: string) =>
    request<ApiResponse<OrderBook>>(`/api/market/orderbook/${symbol}`),
  
  getChartData: (
    symbol: string,
    interval: string = '1h',
    range: string = '7d'
  ) =>
    request<ApiResponse<ChartData>>(
      `/api/market/chart/${symbol}?interval=${interval}&range=${range}`
    ),
};

// Risk API
export const riskApi = {
  getMetrics: () => request<ApiResponse<RiskMetrics>>('/api/risk/metrics'),
  
  getLimits: () => request<ApiResponse<RiskLimit[]>>('/api/risk/limits'),
  
  updateLimit: (id: string, limit: number) =>
    request<ApiResponse<RiskLimit>>(`/api/risk/limits/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ limit }),
    }),
};

// System API
export const systemApi = {
  getStatus: () => request<ApiResponse<SystemStatus>>('/api/system/status'),
  
  getHealth: () => request<ApiResponse<{ status: string }>>('/api/health'),
};

// Alerts API
export const alertsApi = {
  getAll: (params?: { acknowledged?: boolean }) => {
    const query = new URLSearchParams(
      Object.entries(params || {}).reduce((acc, [k, v]) => {
        if (v !== undefined) acc[k] = String(v);
        return acc;
      }, {} as Record<string, string>)
    ).toString();
    return request<ApiResponse<Alert[]>>(
      `/api/alerts${query ? `?${query}` : ''}`
    );
  },
  
  acknowledge: (id: string) =>
    request<ApiResponse<Alert>>(`/api/alerts/${id}/acknowledge`, {
      method: 'POST',
    }),
  
  acknowledgeAll: () =>
    request<ApiResponse<void>>('/api/alerts/acknowledge-all', {
      method: 'POST',
    }),
};

// Settings API
export const settingsApi = {
  get: () => request<ApiResponse<UserSettings>>('/api/settings'),
  
  update: (settings: Partial<UserSettings>) =>
    request<ApiResponse<UserSettings>>('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),
};

// =============================================================================
// Kill Switch API
// =============================================================================
// 
// GAP-KILLSWITCH-001: Path mismatch
// - Frontend uses: /api/kill-switch/*
// - Backend has aliases at: /api/kill-switch/* → /tool/kill_switch/*
// - Paths are now aligned via backend aliases
//
// GAP-KILLSWITCH-002: Response format mismatch
// - Backend returns: {kill_switch: "on"|"off"} or {ok: true, ...}
// - Frontend expects: {active: boolean}
// - Transformation handled via transformKillSwitchStatus/transformKillSwitchAction

export const killSwitchApi = {
  /**
   * Activate the kill switch
   * POST /api/kill-switch/activate
   * @returns Promise with kill switch status
   */
  activate: async (): Promise<TransformedKillSwitchStatus> => {
    const response = await request<{ active: boolean; message?: string; timestamp?: string }>(
      '/api/kill-switch/activate',
      { method: 'POST' }
    );
    return {
      active: true,
      status: 'active',
      timestamp: response.timestamp ?? new Date().toISOString(),
    };
  },

  /**
   * Deactivate the kill switch
   * POST /api/kill-switch/deactivate
   * @returns Promise with kill switch status
   */
  deactivate: async (): Promise<TransformedKillSwitchStatus> => {
    const response = await request<{ active: boolean; message?: string; timestamp?: string }>(
      '/api/kill-switch/deactivate',
      { method: 'POST' }
    );
    return {
      active: false,
      status: 'inactive',
      timestamp: response.timestamp ?? new Date().toISOString(),
    };
  },

  /**
   * Get kill switch status
   * GET /api/kill-switch/status
   * @returns Promise with kill switch status
   */
  getStatus: async (): Promise<TransformedKillSwitchStatus> => {
    const response = await request<{ active: boolean; status?: string; timestamp?: string }>(
      '/api/kill-switch/status'
    );
    return {
      active: response.active,
      status: response.active ? 'active' : 'inactive',
      timestamp: response.timestamp ?? new Date().toISOString(),
    };
  },
};

// =============================================================================
// Proposals API
// =============================================================================
//
// GAP-PROPOSALS-001: Naming mismatch (proposals vs intents)
// - Frontend uses: /api/proposals/*
// - Backend uses: /tool/intent/* for internal logic
// - Backend has aliases at: /api/proposals/* → /tool/intent/*
// - Paths are now aligned via backend aliases
//
// GAP-PROPOSALS-002: Response format
// - Backend returns intent objects with snake_case fields
// - Frontend expects proposal objects with camelCase fields
// - Transformation handled via transformProposal/transformProposals

export const proposalsApi = {
  /**
   * Get all pending proposals
   * GET /api/proposals
   * @returns Promise with array of proposals
   */
  getAll: async (): Promise<TransformedProposal[]> => {
    const response = await request<{ items: Record<string, unknown>[] }>('/api/proposals');
    return transformProposals(response.items as any[]);
  },

  /**
   * Create a new proposal
   * POST /api/proposals
   * @param order - The order details for the proposal
   * @returns Promise with the created proposal
   */
  create: async (order: {
    symbol?: string;
    side?: 'buy' | 'sell' | 'long' | 'short';
    type?: 'market' | 'limit' | 'stop' | 'stop_limit';
    quantity?: number;
    size?: number;
    price?: number;
    stopPrice?: number;
  }): Promise<TransformedProposal> => {
    const response = await request<{ id: string; order: Partial<Order>; created_at?: string }>(
      '/api/proposals',
      {
        method: 'POST',
        body: JSON.stringify(order),
      }
    );
    return {
      id: response.id,
      order: {
        symbol: order.symbol ?? '',
        side: order.side ?? 'buy',
        type: order.type ?? 'market',
        quantity: order.quantity ?? 0,
        price: order.price,
      },
      status: 'pending',
      createdAt: (response as any).created_at ?? new Date().toISOString(),
      expiresAt: '',
      riskAssessment: {
        projectedPnL: 0,
        riskScore: 0.5,
        warnings: [],
      },
      rationale: '',
    };
  },

  /**
   * Get a single proposal by ID
   * GET /api/proposals/:id
   * @param id - The proposal ID
   * @returns Promise with the proposal
   */
  get: async (id: string): Promise<TransformedProposal> => {
    const response = await request<Record<string, unknown>>(`/api/proposals/${id}`);
    return transformProposal(response as any);
  },

  /**
   * Confirm (approve) a proposal
   * POST /api/proposals/:id/confirm
   * @param id - The proposal ID
   * @returns Promise with the confirmed proposal
   */
  confirm: async (id: string): Promise<TransformedProposal> => {
    const response = await request<Record<string, unknown>>(
      `/api/proposals/${id}/confirm`,
      { method: 'POST' }
    );
    return transformProposal(response as any);
  },

  /**
   * Reject a proposal
   * POST /api/proposals/:id/reject
   * @param id - The proposal ID
   * @returns Promise<void>
   */
  reject: async (id: string): Promise<void> => {
    await request<void>(`/api/proposals/${id}/reject`, { method: 'POST' });
  },
};

export interface ReppoPipelineRun {
  run_id: string;
  dataset_id: string;
  snapshot_date: string;
  tier: string;
  status: string;
  stage?: string | null;
  result?: string | null;
  requested_by: string;
  request_source: string;
  replay_of?: string | null;
  source_data_hash?: string | null;
  artifact_uri?: string | null;
  pod_id?: string | null;
  error?: string | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  state: Record<string, unknown>;
}

export interface ReppoHitlEvent {
  id: string;
  type: string;
  run_id: string;
  requested_by: string;
  requested_at: string;
  expires_at: string;
  payload: Record<string, unknown>;
  state: 'pending' | 'approved' | 'rejected' | 'expired';
  decided_by?: string | null;
  decided_role?: string | null;
  decided_at?: string | null;
  decision_reason?: string | null;
}

export interface ReppoDatasetCatalogItem {
  dataset_id: string;
  display_name: string;
  bundle: string;
  stage: 'public_bundle' | 'public_free' | 'side_channel_pilot' | 'expansion' | 'removed';
  rank?: number | null;
  launch_role: string;
  legal_status: 'derived_allowed' | 'requires_review' | 'blocked' | 'removed';
  source_status: 'available' | 'planned' | 'conditional' | 'removed';
  scheduled_by_default: boolean;
  enabled: boolean;
  notes?: string | null;
}

export const reppoPipelineApi = {
  runs: (limit = 50) => request<ReppoPipelineRun[]>(`/api/reppo-pipeline/runs?limit=${limit}`),
  datasets: (includeRemoved = true) =>
    request<ReppoDatasetCatalogItem[]>(`/api/reppo-pipeline/datasets?include_removed=${includeRemoved}`),
  hitl: () => request<ReppoHitlEvent[]>('/api/reppo-pipeline/hitl'),
  run: (payload: { dataset_id?: string; snapshot_date?: string; tier?: string; reason: string }) =>
    request<ReppoPipelineRun>('/api/reppo-pipeline/run', { method: 'POST', body: JSON.stringify(payload) }),
  runRange: (payload: { dataset_id?: string; start_date: string; end_date: string; tier?: string; reason: string }) =>
    request<ReppoPipelineRun[]>('/api/reppo-pipeline/run-range', { method: 'POST', body: JSON.stringify(payload) }),
  replay: (payload: { run_id: string; reason: string }) =>
    request<ReppoPipelineRun>('/api/reppo-pipeline/replay', { method: 'POST', body: JSON.stringify(payload) }),
  rollback: (payload: { pod_id: string; prior_good_pod_id: string; run_id?: string; reason: string }) =>
    request<ReppoHitlEvent>('/api/reppo-pipeline/rollback', { method: 'POST', body: JSON.stringify(payload) }),
  pause: (reason: string) =>
    request<Record<string, unknown>>('/api/reppo-pipeline/pause', { method: 'POST', body: JSON.stringify({ reason }) }),
  resume: (reason: string) =>
    request<Record<string, unknown>>('/api/reppo-pipeline/resume', { method: 'POST', body: JSON.stringify({ reason }) }),
  decide: (eventId: string, payload: { decision: 'approve' | 'reject'; role: string; reason: string }) =>
    request<Record<string, unknown>>(`/api/reppo-pipeline/approvals/${encodeURIComponent(eventId)}/decision`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  verifyAudit: () =>
    request<Record<string, unknown>>('/api/reppo-pipeline/audit/verify', { method: 'POST' }),
};

// Export all APIs
export const api = {
  portfolio: portfolioApi,
  orders: ordersApi,
  trades: tradesApi,
  signals: signalsApi,
  market: marketApi,
  risk: riskApi,
  system: systemApi,
  alerts: alertsApi,
  settings: settingsApi,
  killSwitch: killSwitchApi,
  proposals: proposalsApi,
  reppoPipeline: reppoPipelineApi,
};

export { ApiError };
export default api;
